import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { formatDuration } from '../../utils/format';
import { api } from '../../App';
import { toast } from 'sonner';
import { MessageSquare, RotateCcw, FileText, CheckCircle, X, CalendarClock, MapPin, AlertTriangle, BadgePoundSterling } from 'lucide-react';
import { formatAddress } from '../../utils/format';
import { RescheduleDialog } from './RescheduleDialog';

export function BookingCard({ booking, variant, onOpenConsultation, reviewSlot, onBookingUpdated, autoOpenReport = false }) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refundPreview, setRefundPreview] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');

  const ref = booking.booking_ref || booking.booking_id;

  const fetchRefundPreview = useCallback(async () => {
    if (booking.payment_status !== 'paid') return;
    setRefundLoading(true);
    try {
      const res = await api.get(`/bookings/${booking.booking_id}/refund-preview`);
      setRefundPreview(res.data);
    } catch (err) {
      console.error('Refund preview failed:', err);
    } finally { setRefundLoading(false); }
  }, [booking.booking_id, booking.payment_status]);

  useEffect(() => {
    if (cancelOpen) fetchRefundPreview();
  }, [cancelOpen, fetchRefundPreview]);

  useEffect(() => {
    if (autoOpenReport && booking.auto_completed && booking.status === 'completed') {
      setReportIssueOpen(true);
    }
  }, [autoOpenReport, booking.auto_completed, booking.status]);

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      const res = await api.post(`/bookings/${booking.booking_id}/cancel`, { reason: cancelReason });
      const refund = res.data?.refund;
      if (refund?.eligible && refund?.refund_amount > 0) {
        toast.success(`Booking cancelled. £${refund.refund_amount.toFixed(2)} refund issued`);
      } else {
        toast.success('Booking cancelled');
      }
      setCancelOpen(false);
      onBookingUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally { setSubmitting(false); }
  };

  const handleReportIssue = async () => {
    const trimmed = reportReason.trim();
    if (trimmed.length < 8) {
      toast.error('Please add a bit more detail so we can help');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/bookings/${booking.booking_id}/report-issue`, { reason: trimmed });
      toast.success(res.data?.message || 'Report submitted, our team will be in touch');
      setReportIssueOpen(false);
      setReportReason('');
      onBookingUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally { setSubmitting(false); }
  };

  if (variant === 'cancelled') {
    const refund = booking.refund;
    return (
      <div className="card-surface p-4 sm:p-6 opacity-60">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Cancelled</span>
              <span className="text-xs text-stone-400 font-mono">{ref}</span>
            </div>
            <h3 className="font-heading text-xl font-medium text-[#1A1A1A]">{booking.service_name}</h3>
            <p className="text-stone-500">with {booking.provider_name}</p>
            {booking.cancellation_reason && (
              <p className="text-xs text-stone-400 mt-1">Reason: {booking.cancellation_reason}</p>
            )}
            {refund && refund.amount > 0 && (
              <p className="text-xs text-green-600 mt-1 font-medium" data-testid={`refund-badge-${booking.booking_id}`}>
                £{refund.amount.toFixed(2)} refunded ({refund.percentage}%)
              </p>
            )}
            {refund && !refund.eligible && refund.reason && !refund.reason.includes('Stripe') && !refund.reason.includes('rk_live') && (
              <p className="text-xs text-amber-600 mt-1">{refund.reason.split('(')[0].trim()}</p>
            )}
          </div>
          <div className="text-left md:text-right flex items-center gap-3">
            <p className="text-stone-500">{booking.date} at {booking.time}</p>
            <Link to={`/service/${booking.service_id}`}>
              <Button variant="outline" size="sm" className="border-[#D4AF37] text-[#D4AF37] hover:bg-amber-50 gap-1" data-testid={`rebook-cancelled-${booking.booking_id}`}>
                <RotateCcw className="h-3.5 w-3.5" />Rebook
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'completed') {
    const isAutoCompleted = !!booking.auto_completed;
    const disputedAlready = booking.status === 'disputed';
    const autoAt = booking.auto_completed_at ? new Date(booking.auto_completed_at) : null;
    const DISPUTE_WINDOW_H = 48;
    const disputeDeadline = autoAt ? new Date(autoAt.getTime() + DISPUTE_WINDOW_H * 3600 * 1000) : null;
    const withinDisputeWindow = !!(disputeDeadline && Date.now() < disputeDeadline.getTime());
    const hoursLeft = disputeDeadline ? Math.max(0, Math.ceil((disputeDeadline.getTime() - Date.now()) / 3600000)) : 0;
    return (
      <div className="card-surface p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs text-stone-400 font-mono">{ref}</span>
              {isAutoCompleted && !disputedAlready && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid={`auto-completed-badge-${booking.booking_id}`}>
                  <CheckCircle className="h-3 w-3" />Auto-completed
                </span>
              )}
              {disputedAlready && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full" data-testid={`disputed-badge-${booking.booking_id}`}>
                  <AlertTriangle className="h-3 w-3" />Issue reported, under review
                </span>
              )}
            </div>
            <h3 className="font-heading text-xl font-medium text-[#1A1A1A]">{booking.service_name}</h3>
            <p className="text-stone-500">with {booking.provider_name}</p>
            <p className="text-sm text-stone-400 mt-1">{booking.date} at {booking.time}</p>
            {isAutoCompleted && !disputedAlready && withinDisputeWindow && (
              <p className="text-xs text-amber-700 mt-2 flex items-center gap-1" data-testid={`auto-complete-notice-${booking.booking_id}`}>
                <AlertTriangle className="h-3.5 w-3.5" />
                Marked completed automatically. Report an issue within {hoursLeft}h.
              </p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAutoCompleted && !disputedAlready && withinDisputeWindow && (
              <Button
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 gap-1"
                onClick={() => setReportIssueOpen(true)}
                data-testid={`report-issue-btn-${booking.booking_id}`}
              >
                <AlertTriangle className="h-4 w-4" />Report Issue
              </Button>
            )}
            <Link to={`/service/${booking.service_id}`}>
              <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-amber-50 gap-1" data-testid={`rebook-${booking.booking_id}`}>
                <RotateCcw className="h-4 w-4" />Rebook
              </Button>
            </Link>
            {booking.provider_user_id && (
              <Link to={`/messages?user=${booking.provider_user_id}`}>
                <Button variant="outline" className="border-[#E6E4DD]">
                  <MessageSquare className="h-4 w-4 mr-2" />Message
                </Button>
              </Link>
            )}
            {reviewSlot}
          </div>
        </div>

        {/* Report Issue Dialog */}
        <Dialog open={reportIssueOpen} onOpenChange={setReportIssueOpen}>
          <DialogContent className="sm:max-w-md" data-testid={`report-issue-dialog-${booking.booking_id}`}>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Report an issue
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <p className="text-sm text-stone-600">
                Your {booking.service_name} booking with {booking.provider_name} was marked completed automatically. Please describe what went wrong, our team will review within 1 working day, and the stylist's earnings for this booking are on hold while we investigate.
              </p>
              <Textarea
                placeholder="Tell us what happened (e.g. appointment didn't go ahead, service was incomplete, unhappy with result...)"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                rows={5}
                maxLength={2000}
                className="border-[#E6E4DD]"
                data-testid={`report-issue-reason-${booking.booking_id}`}
              />
              <p className="text-[11px] text-stone-400">{reportReason.length}/2000 characters · You have {hoursLeft}h left to report this.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReportIssueOpen(false)} disabled={submitting}>Cancel</Button>
                <Button
                  onClick={handleReportIssue}
                  disabled={submitting || reportReason.trim().length < 8}
                  className="bg-red-500 hover:bg-red-600 text-white gap-1"
                  data-testid={`report-issue-submit-${booking.booking_id}`}
                >
                  <AlertTriangle className="h-4 w-4" />
                  {submitting ? 'Sending...' : 'Submit report'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Default: upcoming/confirmed/pending
  const isRescheduleRequested = booking.status === 'reschedule_requested';
  const canManage = booking.status !== 'pending_payment';

  return (
    <div className="card-surface p-4 sm:p-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
              isRescheduleRequested ? 'bg-purple-100 text-purple-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>{isRescheduleRequested ? 'Reschedule Requested' : booking.status}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${booking.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'}`}>{booking.payment_status === 'paid' ? 'Paid' : 'Unpaid'}</span>
            <span className="text-xs text-stone-400 font-mono">{ref}</span>
          </div>
          <h3 className="font-heading text-xl font-medium text-[#1A1A1A]">{booking.service_name}</h3>
          <p className="text-stone-500">with {booking.provider_name}</p>
          {booking.provider_service_address && booking.payment_status === 'paid' && (
            <p className="text-sm text-stone-500 mt-1 flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" />
              {formatAddress(booking.provider_service_address)}
            </p>
          )}
        </div>
        <div className="text-left md:text-right">
          <p className="font-medium text-[#1A1A1A]">{booking.date}</p>
          <p className="text-stone-500">{booking.time} {booking.duration ? `• ${formatDuration(booking.duration)}` : ''}</p>
          <p className="text-[#D4AF37] font-semibold mt-1">£{booking.price}</p>
        </div>
      </div>

      {isRescheduleRequested && booking.reschedule_request && (
        <div className="mt-3 p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
          Reschedule requested: {booking.reschedule_request.new_date} at {booking.reschedule_request.new_time}, awaiting provider response
        </div>
      )}

      {booking.notes && (
        <div className="mt-4 pt-4 border-t border-[#E6E4DD]">
          <p className="text-sm text-stone-500">Notes: {booking.notes}</p>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-[#E6E4DD] flex flex-wrap items-center gap-3">
        {booking.addons && booking.addons.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {booking.addons.map((a) => (
              <span key={a.addon_id || a.name} className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded border border-amber-200">{a.name} +£{a.price?.toFixed(2)}</span>
            ))}
          </div>
        )}
        {booking.consultation_form_id && !booking.consultation_submitted && (
          <Button variant="outline" size="sm" className="gap-1 border-[#D4AF37] text-[#D4AF37] hover:bg-amber-50 ml-auto" onClick={() => onOpenConsultation(booking)} data-testid={`fill-consultation-${booking.booking_id}`}>
            <FileText className="h-3.5 w-3.5" />Fill Consultation Form
          </Button>
        )}
        {booking.consultation_submitted && (
          <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" />Consultation submitted</span>
        )}

        {/* Cancel & Reschedule actions */}
        {canManage && !isRescheduleRequested && (
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" size="sm" className="gap-1 border-stone-200 text-stone-500 hover:text-red-600 hover:border-red-200" onClick={() => setCancelOpen(true)} data-testid={`cancel-booking-${booking.booking_id}`}>
              <X className="h-3.5 w-3.5" />Cancel
            </Button>
            <Button variant="outline" size="sm" className="gap-1 border-[#D4AF37] text-[#D4AF37] hover:bg-amber-50" onClick={() => setRescheduleOpen(true)} data-testid={`reschedule-booking-${booking.booking_id}`}>
              <CalendarClock className="h-3.5 w-3.5" />Reschedule
            </Button>
          </div>
        )}
      </div>

      {/* Cancel Dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Cancel Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-stone-600">Are you sure you want to cancel your booking for <strong>{booking.service_name}</strong> on {booking.date}?</p>

            {/* Refund Preview */}
            {booking.payment_status === 'paid' && (
              <div data-testid="refund-preview-section">
                {refundLoading ? (
                  <div className="p-3 bg-stone-50 rounded-lg text-sm text-stone-500 animate-pulse">Calculating refund...</div>
                ) : refundPreview ? (
                  <div className={`p-3 rounded-lg text-sm ${refundPreview.eligible ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {refundPreview.eligible ? (
                        <BadgePoundSterling className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                      )}
                      <span className={`font-medium ${refundPreview.eligible ? 'text-green-700' : 'text-amber-700'}`}>
                        {refundPreview.eligible
                          ? `£${refundPreview.refund_amount.toFixed(2)} refund`
                          : 'No refund'}
                      </span>
                    </div>
                    <p className={`text-xs ${refundPreview.eligible ? 'text-green-600' : 'text-amber-600'}`}>
                      {refundPreview.reason}
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-[#1A1A1A] mb-1 block">Reason (optional)</label>
              <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Let the provider know why..." className="input-zuriya" data-testid="cancel-reason-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep Booking</Button>
              <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={handleCancel} disabled={submitting} data-testid="confirm-cancel-btn">
                {submitting ? 'Cancelling...' : 'Cancel Booking'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog, uses the provider's live calendar to prevent conflicts */}
      <RescheduleDialog
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        booking={booking}
        onRescheduled={onBookingUpdated}
      />
    </div>
  );
}
